import DOMPurify, { type Config } from 'dompurify';

/**
 * Central sanitization layer for listing-provided (untrusted) content.
 *
 * Listing text/URL fields originate from external sources and must never be
 * rendered into HTML, anchors or iframes without validation. `sanitizeHtml`
 * strips everything outside a small allowlist of formatting tags; `sanitizeUrl`
 * rejects any URL whose scheme is not explicitly safe (blocks `javascript:`,
 * `data:`, `vbscript:`, ...).
 *
 * SSR note: DOMPurify needs a DOM, which the render lambda (bare Node, no
 * `window`) does not have. We do not ship a server-side DOM because the two
 * rich-text fields rendered via `v-html` — the listing description and the
 * viewing note — are already HTML-sanitised upstream by listing-api's strict
 * `sanitize-html` allowlist whenever the request carries `?sanitize=true`, which
 * the listing store always sends. During SSR `sanitizeHtml` therefore passes the
 * value through untouched and listing-api is the effective control for the
 * server-rendered first paint. The browser DOMPurify pass is NOT a second line of
 * defence for that first paint — the SSR HTML is parsed (and any inline handler
 * such as `onerror` fires) before hydration runs — it only re-sanitises content
 * that is rendered or updated client-side (SPA navigation, reactive changes).
 * Because of that, the client allowlist below MUST stay a superset of
 * listing-api's `allowedTags.listingDetails`; if it strips a tag listing-api
 * keeps, hydration mismatches and the content disappears after hydration.
 *
 * The gaps listing-api does not cover — URL schemes, the virtual-tour iframe, and
 * the MaskedDescription template-injection path — are closed by `sanitizeUrl`, the
 * virtual-tour host allowlist, and the MaskedDescription refactor, none of which
 * require a DOM. See KK-2900.
 */

const ACW_RESPONSE_DATA_ID = 'acw_response_data';
const ACW_RESPONSE_DATA_ALLOWED_ATTRIBUTES = new Set(['id', 'hidden', 'style']);
const DEFAULT_SPAN_ALLOWED_ATTRIBUTES = new Set(['class']);

// Client only: new-tab anchors must not leak the opener/referrer.
if (!import.meta.env.SSR) {
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    const element = node as unknown as {
      getAttribute?: (name: string) => string | null;
      setAttribute?: (name: string, value: string) => void;
    };
    if (element.getAttribute?.('target') === '_blank') {
      element.setAttribute?.('rel', 'noopener noreferrer');
    }
  });

  DOMPurify.addHook('uponSanitizeAttribute', (node, hookEvent) => {
    const tagName = node.tagName.toLowerCase();
    const attributeName = hookEvent.attrName.toLowerCase();

    if (tagName !== 'span') {
      if (ACW_RESPONSE_DATA_ALLOWED_ATTRIBUTES.has(attributeName)) {
        hookEvent.keepAttr = false;
      }
      return;
    }

    const allowedAttributes =
      node.getAttribute('id') === ACW_RESPONSE_DATA_ID
        ? ACW_RESPONSE_DATA_ALLOWED_ATTRIBUTES
        : DEFAULT_SPAN_ALLOWED_ATTRIBUTES;

    if (!allowedAttributes.has(attributeName)) {
      hookEvent.keepAttr = false;
    }
  });
}

/**
 * Allowlist for listing descriptions and viewing notes rendered via `v-html`.
 *
 * It MUST remain a superset of listing-api's `allowedTags.listingDetails`
 * (b, i, em, u, br, ul, ol, li, h1–h6, strong, p — see listing-api
 * `src/api/listing/post-process.ts`). listing-api is the SSR chokepoint, so any
 * tag it emits that we strip here would only be removed on the client and cause a
 * hydration mismatch (content vanishing after hydration). `span` is emitted by
 * listing-api for the ACW response marker and is also injected downstream for
 * tenant-plus masking; `div`/`a` are additional tags injected by us
 * (`wrapUrlsInAnchors`) on both render paths, so they are safe extras rather
 * than a divergence.
 *
 * Everything outside the list — `<script>`, `<img onerror>`, `<iframe>`, event
 * handlers, unknown schemes — is removed. Widen only in lockstep with listing-api.
 */
const RICH_TEXT_CONFIG: Config = {
  ALLOWED_TAGS: [
    'br',
    'a',
    'p',
    'ul',
    'ol',
    'li',
    'span',
    'div',
    'b',
    'strong',
    'i',
    'em',
    'u',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
  ],
  // id/hidden/style are restricted to the exact ACW response span by the hook above.
  ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'id', 'hidden', 'style'],
  // hrefs are limited to http(s)/mailto/tel; this alone neutralises javascript:/data: URIs.
  ALLOWED_URI_REGEXP: /^(?:https?|mailto|tel):/i,
  // ALLOWED_URI_REGEXP is also applied to non-URI attributes, which would strip
  // `target="_blank"`; mark `target` URI-safe so the new-tab behaviour survives.
  ADD_URI_SAFE_ATTR: ['target'],
};

/**
 * Sanitize untrusted rich-text HTML (listing description, viewing note, ...)
 * against {@link RICH_TEXT_CONFIG} before it is rendered via `v-html`.
 *
 * @param dirty - raw HTML string (may be empty/undefined)
 * @param config - optional override of the default allowlist
 * @returns a safe HTML string (never `undefined`)
 */
export function sanitizeHtml(dirty?: string | null, config: Config = RICH_TEXT_CONFIG): string {
  if (!dirty) {
    return '';
  }
  // SSR has no DOM; content is pre-sanitised upstream by listing-api (see file header).
  if (import.meta.env.SSR) {
    return dirty;
  }
  return DOMPurify.sanitize(dirty, config) as string;
}

const SAFE_URL_SCHEMES = new Set(['https:', 'http:', 'mailto:', 'tel:']);
// Host is irrelevant for the scheme check; used only to make relative URLs parseable.
const URL_PARSE_BASE = 'https://www.homegate.ch';
const HAS_SCHEME_REGEXP = /^[a-z][a-z0-9+.-]*:/i;

/**
 * Validate an untrusted URL before it is bound to an `href`/`src`.
 *
 * Returns `undefined` when the URL is missing, malformed, uses a disallowed
 * scheme (e.g. `javascript:`, `data:`), or is relative while `allowRelative`
 * is false. Callers must treat `undefined` as "no link" and hide the element.
 *
 * Parsing normalises the value (stripping embedded tab/newline characters that
 * could smuggle a `javascript:` scheme past a naive check), so the returned
 * string is the canonical form rather than the raw input.
 *
 * @param raw - the untrusted URL string
 * @param allowRelative - permit scheme-less relative URLs (e.g. attachment paths)
 */
export function sanitizeUrl(
  raw?: string | null,
  { allowRelative = false }: { allowRelative?: boolean } = {},
): string | undefined {
  if (!raw) {
    return undefined;
  }
  const value = raw.trim();
  if (!value) {
    return undefined;
  }

  let parsed: URL;
  try {
    parsed = new URL(value, URL_PARSE_BASE);
  } catch {
    return undefined;
  }
  if (!SAFE_URL_SCHEMES.has(parsed.protocol)) {
    return undefined;
  }

  const isAbsolute = HAS_SCHEME_REGEXP.test(value) || value.startsWith('//');
  if (isAbsolute) {
    return parsed.href;
  }
  if (!allowRelative) {
    return undefined;
  }
  // Relative URL with a safe (inherited) scheme: preserve the original form so
  // its resolution against the current page is unchanged. Dangerous schemes —
  // including control-character-obfuscated ones like "java\nscript:" — were
  // already rejected above via parsed.protocol.
  return value;
}
